import {PageAnimation} from '@PosterWhiteboard/page/animation/page-animation.class';
import type {AnimationObject} from '@PosterWhiteboard/classes/item-animation.class';
import {AnimateItemType, AnimationType, getAnimationTime} from '@PosterWhiteboard/classes/item-animation.class';
import {calculatePageDuration} from '@PosterWhiteboard/libraries/video-page';
import {isAnimationApplicableOnItem} from '@PosterWhiteboard/animation/animation.class';
import {showMessageGrowl} from '@Components/message-growl';

const END_TIME_LEEWAY = 4;
const DELAY_FACTOR = 0.2;

export class PageOutroAnimation extends PageAnimation {
  public override replayPoster(): void {
    void this.page.poster.stop();
    void this.page.poster.seek(this.getTimeToSeekForReplay());
    void this.page.poster.play();
  }

  private showAnimationReplacedMessage(oldAnimation: AnimationObject): void {
    let showAnimationsReplacedMessage = false;
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (
        item.hasOutroAnimation() &&
        (item.outroAnimation.type !== oldAnimation.type || item.outroAnimation.speed !== oldAnimation.speed || item.outroAnimation.direction !== oldAnimation.direction)
      ) {
        showAnimationsReplacedMessage = true;
      }
    });
    if (showAnimationsReplacedMessage) {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_animations_replaced'),
        interval: 2000,
      });
    }
  }

  public override updatePageItemsAnimation(oldAnimation: AnimationObject | null): void {
    if (oldAnimation) {
      this.showAnimationReplacedMessage(oldAnimation);
    }
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (isAnimationApplicableOnItem(item, this.type)) {
        void item.updateFromObject({outroAnimation: this.toObject()}, {undoable: false, updateRedux: false, doInvalidate: false, fireModifiedEvent: false});
      } else if (item.hasOutroAnimation()) {
        void item.updateFromObject({outroAnimation: {type: AnimationType.NONE}}, {undoable: false, updateRedux: false, doInvalidate: false, fireModifiedEvent: false});
      }
    });
    this.updateItemDuration(calculatePageDuration(this.page));
  }

  public getEndTimesForOutroAnimations(pageDuration: number): Record<string, number> {
    const endTimeHashMap: Record<string, number> = {};
    let maxAnimationDuration = 0;
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const currentItem = this.page.items.itemsHashMap[key];
      switch (currentItem.outroAnimation.type) {
        case AnimationType.RISE: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.OUTRO_ANIMATION, pageDuration);
          endTimeHashMap[currentItem.uid] =
            this.getDelayFactorByTotalObjects(getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.BLOCK: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.OUTRO_ANIMATION, pageDuration).filter((item) => item.isText() || item.isSlideshow());
          if (items.length) {
            endTimeHashMap[currentItem.uid] =
              this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed)) *
                items.findIndex((item) => currentItem.uid === item.uid) -
              getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed) * 0.5;
          }
          break;
        }
        case AnimationType.TUMBLE: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.OUTRO_ANIMATION, pageDuration);
          endTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          void currentItem.updateFromObject(
            {outroAnimation: {direction: this.getTumbleAnimationDirection(currentItem)}},
            {doInvalidate: false, undoable: false, updateRedux: false}
          );
          break;
        }
        case AnimationType.PEEK:
        case AnimationType.WIPE:
        case AnimationType.PAN: {
          const items = this.getSortedGraphicItemsAccordingToPosition(AnimateItemType.OUTRO_ANIMATION, pageDuration);
          endTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.POP:
        case AnimationType.JELLO: {
          const items = this.getSortedGraphicItemsAccordingToZIndex(AnimateItemType.OUTRO_ANIMATION, pageDuration);
          endTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        case AnimationType.ROTATE: {
          const items = this.getSortedGraphicItemsAccordingToZIndex(AnimateItemType.OUTRO_ANIMATION, pageDuration);
          endTimeHashMap[currentItem.uid] =
            this.getAlternativeDelayFactor(items.length, getAnimationTime(currentItem.outroAnimation.type, currentItem.outroAnimation.speed)) *
            items.findIndex((item) => currentItem.uid === item.uid);
          break;
        }
        default:
          endTimeHashMap[currentItem.uid] = 0;
      }
      maxAnimationDuration = Math.max(maxAnimationDuration, endTimeHashMap[currentItem.uid]);
      endTimeHashMap[currentItem.uid] += this.itemHasDifferentEndTime(currentItem, pageDuration) ? currentItem.getDuration() : pageDuration;
    });
    Object.keys(endTimeHashMap).forEach((itemId) => {
      const item = this.page.items.itemsHashMap[itemId];
      if (item.hasOutroAnimation()) {
        endTimeHashMap[itemId] -= this.itemHasDifferentEndTime(item, pageDuration) ? 0 : maxAnimationDuration;
        endTimeHashMap[itemId] -= endTimeHashMap[itemId] === pageDuration ? DELAY_FACTOR : 0;
      }
    });
    this.zIndexSortedItems = [];
    this.positionSortedItems = [];
    return endTimeHashMap;
  }

  public updateItemDuration(duration?: number): void {
    const pageDuration = duration ?? this.page.getDuration();
    const endTimes = this.getEndTimesForOutroAnimations(pageDuration);
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (this.isAnimationDurationApplicableOnItem(item)) {
        void item.updateFromObject({onPageEndTime: endTimes[key]}, {undoable: false, doInvalidate: false, updateRedux: false, fireModifiedEvent: false});
      }
    });
  }

  private getTimeToSeekForReplay(): number {
    let minEndTime = this.page.duration;
    Object.keys(this.page.items.itemsHashMap).forEach((key) => {
      const item = this.page.items.itemsHashMap[key];
      if (item.onPageEndTime > this.page.duration - END_TIME_LEEWAY) {
        minEndTime = Math.min(minEndTime, item.onPageEndTime - getAnimationTime(item.outroAnimation.type, item.outroAnimation.speed));
      }
    });
    return Math.max(0, minEndTime - DELAY_FACTOR);
  }
}
